import StateObserver, { now, user } from 'src/StateObserver';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { withLoading } from 'src/lib/utils';
import {
  buddiesGetCommonProps,
  buddiesGetDisplayedInvites,
  buddiesGetEvent,
  buddiesGetEventRewardDetails,
  buddiesGetMilestone,
  buddiesGetPartyScore,
  buddiesGetPartyScoreAdded,
  buddiesGetPastEventWithPendingRewards,
  buddiesHasPendingInvites,
  buddiesIsAvailable,
  buddiesIsEnabled,
  buddiesIsEventCompleted,
  buddiesIsLocked,
  buddiesIsPartyFull,
} from 'src/replicant/buddies/buddies.getters';
import type {
  BuddiesInviteMethod,
  BuddiesViewOrigin,
} from 'src/replicant/buddies/buddies.analytics.schema';
import {
  buddiesSchedule,
  buddiesUnlockLevel,
} from 'src/replicant/buddies/buddies.ruleset';
import { captureGenericError } from 'src/lib/sentry';
import { Feature } from 'src/replicant/analytics';
import { track } from 'src/lib/analytics/utils';
import {
  buddiesGetPartyPlayers,
  buddiesGetUnseenPartyJoinerIds,
} from './buddies.utils';
import i18n from 'src/lib/i18n/i18n';
import { buddiesFetchPlayers } from './buddies.friends';
import { canAutoAddFriend } from 'src/replicant/friends/friends.getters';
import { animatePuzzleRewards } from '../animation/rewardAnimation';
import { GCInstant } from 'src/lib/gcinstant';
import { openProfile } from '../profile/profile.sequence';

/**
 * Open the main popup of the feature.
 */
export async function buddiesOpenMainPopup(origin: BuddiesViewOrigin) {
  // Check if there are any pending rewards to claim from past events
  const pendingEventId = buddiesGetPastEventWithPendingRewards(user(), now());
  if (pendingEventId) {
    await openPopupPromise('popupBuddiesBaking', {
      eventId: pendingEventId,
      origin,
    });
    return;
  }

  // Return if there is no current event
  const currentEventId = buddiesSchedule.getCurrentEvent(now())?.id;
  if (!currentEventId) {
    return;
  }

  // Process incoming invites and show join rewards for new party members
  await buddiesPartyJoinSequence(origin);

  // If the user has not seen the buddies event yet, show the info popup.
  const seenAt = buddiesGetEvent(user(), now())?.seenAt ?? 0;
  if (!seenAt) {
    // Show the buddies info popup, so the user knows what's going on.
    await openPopupPromise('popupBuddiesInfo', {});
    // Mark the event as seen, so the user won't see the info popup again.
    await StateObserver.invoke.buddiesUpdateEventSeenAt();
  }

  // Ensure the user has a public profile before allowing them to open the event
  if (!user().publicProfile) {
    await openProfile(origin);
  }

  // Open the baking popup, with the event already created.
  await openPopupPromise('popupBuddiesBaking', {
    eventId: currentEventId,
    origin,
  });
}

/**
 * Orchestrates the buddies experience on game launch.
 */
export async function buddiesLaunchSequence() {
  if (!buddiesIsAvailable(user(), now()) && !buddiesIsLocked(user(), now()))
    return;

  // Process join rewards only — skip invite popups
  const hasActivity = await buddiesPartyJoinSequence('launch', {
    showInvitePopups: false,
  });

  // Skip opening the main popup if nothing happened
  if (!hasActivity) return;

  // Skip opening the main popup if the feature is locked
  if (buddiesIsLocked(user(), now())) return;

  // Open the main popup to show the updated party
  await buddiesOpenMainPopup('launch');
}

/**
 * Orchestrates the buddies party join flow:
 * 1. Fetches all relevant player info upfront
 * 2. Processes incoming invites one at a time (when showInvitePopups is true)
 * 3. Shows join rewards immediately after each accepted invite
 * 4. Shows join rewards for players that joined before this session
 * 5. Discards remaining invites if party gets full
 *
 * Returns true if any invites were accepted or unseen joiners were found.
 */
async function buddiesPartyJoinSequence(
  origin: BuddiesViewOrigin,
  opts?: { showInvitePopups?: boolean },
): Promise<boolean> {
  const showInvitePopups = opts?.showInvitePopups ?? true;

  // Gather IDs: incoming invites (empty when popups are skipped) + unseen party joiners
  const inviteIds =
    showInvitePopups && buddiesHasPendingInvites(user(), now())
      ? buddiesGetDisplayedInvites(user(), now())
      : [];
  const unseenJoinerIds = buddiesGetUnseenPartyJoinerIds();

  const allPlayerIds = [...new Set([...inviteIds, ...unseenJoinerIds])];
  if (allPlayerIds.length === 0) return false;

  // Fetch all player info upfront (single batch)
  const playersMap = await withLoading(
    () => buddiesFetchPlayers(allPlayerIds),
    { feature: Feature.BUDDIES._, subFeature: Feature.BUDDIES._ },
  );

  // Ensure the user has a public profile before the incoming invites
  if (showInvitePopups && !user().publicProfile) {
    await openProfile(origin);
  }

  // Claim rewards for players that already joined
  if (unseenJoinerIds.length > 0) {
    await StateObserver.invoke.buddiesClaimPartyJoinRewards();
  }

  // If the user has not seen the buddies event yet, show the info popup
  // before any incoming invites, so they understand the feature.
  if (showInvitePopups) {
    const seenAt = buddiesGetEvent(user(), now())?.seenAt ?? 0;
    if (!seenAt && inviteIds.length > 0) {
      await openPopupPromise('popupBuddiesInfo', {});
      await StateObserver.invoke.buddiesUpdateEventSeenAt();
    }
  }

  // Process incoming invites (one popup at a time)
  let hasAccepted = false;
  for (const senderId of inviteIds) {
    if (buddiesIsPartyFull(user(), now())) break;

    const player = playersMap[senderId];
    if (!player) {
      captureGenericError(
        'buddiesPartyJoinSequence: missing player data for invite sender',
        new Error(`Player ${senderId} not found in playersMap`),
      );
      continue;
    }

    const inviteMethod: BuddiesInviteMethod = 'friend';
    const accepted = await buddiesIncomingInviteSequence(
      senderId,
      player.name,
      player.photo,
      inviteMethod,
    );
    if (accepted) hasAccepted = true;
  }

  // Show join rewards for players that joined before this session
  const hasJoiners = unseenJoinerIds.length > 0;
  await buddiesJoinSequence(unseenJoinerIds, playersMap);

  return hasAccepted || hasJoiners;
}

/**
 * Handles a single incoming invite: shows popup and processes accept/decline.
 */
async function buddiesIncomingInviteSequence(
  senderId: string,
  name: string,
  photo: string,
  inviteMethod: BuddiesInviteMethod,
): Promise<boolean> {
  const accepted = await openPopupPromise('popupBuddiesIncomingInvite', {
    name,
    photo,
  });

  if (accepted) {
    const success = await buddiesAcceptInviteSequence(senderId, name, photo);
    if (success) {
      track('BuddiesRequestAccept', {
        ...buddiesGetCommonProps(user(), now()),
        inviteMethod,
        senderId,
      });
      return true;
    }
  } else {
    await withLoading(
      () => StateObserver.invoke.buddiesRemoveInvite({ id: senderId }),
      { feature: Feature.BUDDIES._, subFeature: Feature.BUDDIES._ },
    );
    track('BuddiesRequestReject', {
      ...buddiesGetCommonProps(user(), now()),
      inviteMethod,
      senderId,
    });
  }

  return false;
}

/**
 * Handles the accept path: joins party, auto-adds friend, claims and shows rewards.
 */
async function buddiesAcceptInviteSequence(
  senderId: string,
  name: string,
  photo: string,
): Promise<boolean> {
  const result = await withLoading(
    () => StateObserver.invoke.buddiesAcceptInvite({ senderId }),
    { feature: Feature.BUDDIES._, subFeature: Feature.BUDDIES._ },
  );

  if (!result.success) {
    await openPopupPromise('popupModalWhite', {
      title: i18n('match2.buddies.errorPopup.title'),
      message: i18n(`match2.buddies.errorPopup.${result.reason ?? 'unknown'}`),
      tracking: {
        feature: 'buddies',
        $subFeature: `error_${result.reason}`,
      },
    });
    return false;
  }

  // Auto-add as in-game friend
  const userState = StateObserver.getState().user;
  if (canAutoAddFriend(userState, senderId)) {
    await StateObserver.invoke.addFriendFromInvite({
      inviterId: senderId,
    });
  }

  // Claim reward for this new party member
  await StateObserver.invoke.buddiesClaimPartyJoinRewards();
  return true;
}

/**
 * Shows join rewards popups for players that joined before this session.
 */
async function buddiesJoinSequence(
  joinerIds: string[],
  playersMap: Record<string, { name: string; photo: string }>,
) {
  for (const joinerId of joinerIds) {
    const player = playersMap[joinerId];
    if (!player) {
      captureGenericError(
        'buddiesPartyJoinSequence: missing player data for party joiner',
        new Error(`Player ${joinerId} not found in playersMap`),
      );
      continue;
    }

    await openPopupPromise('popupBuddiesJoinRewards', {
      name: player.name,
      photo: player.photo,
    });
  }
}

/**
 * Run the reward sequence for a buddies event: checks eligibility, shows reward details and claims the reward.
 * Returns true if the reward was successfully claimed, false otherwise (e.g. if already claimed or not eligible).
 */
export async function buddiesRewardSequence(args: { eventId: string }) {
  const { eventId } = args;

  // Get reward details from state
  const details = buddiesGetEventRewardDetails(user(), eventId, now());
  if (!details) {
    return false;
  }

  // Check if already claimed
  if (details.isClaimed) {
    return false;
  }

  const players = await withLoading(
    () => buddiesGetPartyPlayers(args.eventId),
    {
      feature: Feature.BUDDIES._,
      subFeature: Feature.BUDDIES._,
    },
  );

  // Sort by score descending, MVP as tie-breaker
  players.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (a.mvp) return -1;
    if (b.mvp) return 1;
    return 0;
  });

  // Open the reward popup
  await openPopupPromise('popupBuddiesReward', {
    baseGems: details.baseReward,
    totalGems: details.totalReward,
    multiplier: details.multiplier,
    ranking: players,
  });

  return await buddiesClaimRewards(args.eventId);
}

/**
 * Claims the rewards for a buddies event. Should be called after showing the reward details popup.
 * Returns true if the reward was successfully claimed, false otherwise (e.g. if already claimed or not eligible).
 */
async function buddiesClaimRewards(eventId: string) {
  // Get reward details from state
  const details = buddiesGetEventRewardDetails(user(), eventId, now());
  if (!details) {
    return false;
  }

  if (!details.canClaimRewards) {
    return false;
  }

  await withLoading(
    () =>
      StateObserver.invoke.buddiesClaimEventReward({
        eventId,
      }),
    {
      feature: Feature.BUDDIES._,
      subFeature: Feature.BUDDIES._,
    },
  );

  track('BuddiesReward', buddiesGetCommonProps(user(), now(), eventId));

  void animatePuzzleRewards({
    rewards: [
      {
        type: 'gems',
        amount: details.totalReward,
      },
    ],
  });

  return true;
}

/**
 * Checks if the player has reached a new milestone after completing a puzzle and shows the feature popup if so.
 */
export async function buddiesPostPuzzleSequence(hasWon: boolean) {
  if (!hasWon) return;

  if (!buddiesIsAvailable(user(), now())) return;

  // Skip if user is not participating in the current event
  const event = buddiesGetEvent(user(), now());
  if (!event) return;

  const currentEventId = buddiesSchedule.getCurrentEvent(now())?.id;
  if (!currentEventId) return;

  const currentPartyScore = buddiesGetPartyScore(user(), now());
  const previousPartyScore =
    currentPartyScore - buddiesGetPartyScoreAdded(user(), now());

  const currentMilestone = buddiesGetMilestone(currentPartyScore);
  const previousMilestone = buddiesGetMilestone(previousPartyScore);

  if (currentMilestone <= previousMilestone) return;

  await openPopupPromise('popupBuddiesBaking', {
    eventId: currentEventId,
    origin: 'postLevel',
  });
}

/**
 * Shows a locked popup if the user tries to access the feature without meeting the unlock requirements.
 */
export async function buddiesOpenLockedPopup(origin: BuddiesViewOrigin) {
  await openPopupPromise('popupModalWhite', {
    title: i18n('match2.buddies.lockedTitle'),
    message: i18n('match2.buddies.lockedMessage', {
      level: buddiesUnlockLevel,
    }),
    origin,
    tracking: {
      feature: Feature.BUDDIES._,
      $subFeature: Feature.BUDDIES.LOCKED,
    },
  });
}

/**
 * Handles a deep link from a buddies share invite. Shows the incoming invite popup,
 * and on accept auto-connects both users as party buddies.
 * Silently returns early if any step fails, the invite is declined, or the sender cannot be added.
 */
export async function buddiesShareInviteDeepLinkSequence(deepLink?: {
  inviteMethod?: 'line' | 'native';
}) {
  // Find out the sender id
  const senderId =
    GCInstant.entryData?.webReferrer ?? GCInstant.entryData?.playerID;
  if (!senderId) return;
  if (senderId === user().id) return;

  // Can only happen during an active event
  const eventId = buddiesSchedule.getCurrentEvent(now());
  if (!eventId) return;

  // Feature must be enabled for the receiver
  if (!buddiesIsEnabled(user())) return;

  // Return early if event is completed or party is full
  if (buddiesIsEventCompleted(user(), now())) return;
  if (buddiesIsPartyFull(user(), now())) return;

  // Return early if sender is already in our party
  const event = buddiesGetEvent(user(), now());
  if (event?.party[senderId]) return;

  // Fetch sender profile
  const playersMap = await withLoading(() => buddiesFetchPlayers([senderId]), {
    feature: Feature.BUDDIES._,
    subFeature: Feature.BUDDIES._,
  });

  // Return early if sender profile is not found
  const player = playersMap[senderId];
  if (!player) {
    captureGenericError(
      'Buddies invite: sender profile not found',
      new Error(`Player ${senderId} not found in playersMap`),
    );
    return;
  }

  // Ensure user will create their public profile
  if (!user().publicProfile) {
    await openProfile('launch');
  }

  // Show info popup before the invite if user hasn't seen the event
  const seenAt = buddiesGetEvent(user(), now())?.seenAt ?? 0;
  if (!seenAt) {
    await openPopupPromise('popupBuddiesInfo', {});
    await StateObserver.invoke.buddiesUpdateEventSeenAt();
  }

  // Run the incoming invite sequence
  const inviteMethod: BuddiesInviteMethod = deepLink?.inviteMethod ?? 'line';
  await buddiesIncomingInviteSequence(
    senderId,
    player.name,
    player.photo,
    inviteMethod,
  );

  // Skip main popup if feature is still locked
  if (buddiesIsLocked(user(), now())) {
    return;
  }

  // Open the main feature UI
  await buddiesOpenMainPopup('launch');
}
